import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ShieldCheck, Users } from "lucide-react";

import { PageHero, Prose, ProseSection } from "@/components/page-hero";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Quibblies — An adult writing and self-expression community" },
      {
        name: "description",
        content:
          "Quibblies is an 18+ writing, storytelling and self-expression community where every publication, profile image and account is reviewed before it reaches readers.",
      },
      { property: "og:title", content: "About Quibblies" },
      {
        property: "og:description",
        content:
          "Why Quibblies reviews accounts, profile media and writing separately — and what that means for authors and readers.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AboutPage,
});

const PILLARS = [
  {
    icon: Users,
    title: "Account review",
    body: "Registered → Email verified → Pending → Approved. Being approved means you may take part in the community.",
  },
  {
    icon: BookOpen,
    title: "Writing review",
    body: "Draft → Submitted → Reviewing → Approved, Changes requested or Rejected → Published, for every content type. Personal writing does not bypass review.",
  },
  {
    icon: ShieldCheck,
    title: "Profile media review",
    body: "Avatars and cover images are Uploaded → Pending → Approved or Rejected, independently of your account status.",
  },
];

function AboutPage() {
  return (
    <div>
      <PageHero
        eyebrow="About"
        title="A quiet, well-made place to write and read"
        description="Quibblies is a members' community for adult writing of every kind — fiction, personal experiences, reflections, diaries, poetry and serialised work. We are deliberately slow: people, pictures and prose are each reviewed on their own track, by humans, before they appear."
      >
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="hero" size="lg">
            <Link to="/auth" search={{ mode: "register" }}>
              Join Quibblies
            </Link>
          </Button>
          <Button asChild variant="soft" size="lg">
            <Link to="/guidelines">Community guidelines</Link>
          </Button>
        </div>
      </PageHero>

      <div className="mx-auto max-w-5xl px-4 pt-14 sm:px-6">
        <div className="grid gap-5 md:grid-cols-3">
          {PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="rounded-2xl border border-border/70 bg-gradient-card p-6"
            >
              <pillar.icon className="h-5 w-5 text-primary-glow" aria-hidden="true" />
              <h2 className="mt-4 font-display text-xl text-foreground">{pillar.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{pillar.body}</p>
            </div>
          ))}
        </div>
      </div>

      <Prose>
        <ProseSection title="Three separate decisions">
          <p>
            Approving a member is not the same as approving what they upload. A member in good
            standing can still have a cover image declined or a story sent back for changes, and a
            declined image never blocks the rest of the account.
          </p>
        </ProseSection>
        <ProseSection title="Strictly 18+">
          <p>
            Quibblies is for adults. Every member confirms their date of birth and agrees to the
            community guidelines at registration. Accounts that misrepresent age are removed.
          </p>
        </ProseSection>
        <ProseSection title="Authors keep their work">
          <p>
            Version history, autosave recovery and full data export are part of the product, not an
            afterthought. Your writing should never feel trapped inside Quibblies.
          </p>
        </ProseSection>
        <ProseSection title="Appeals">
          <p>
            Every moderation decision can be appealed. Appeals are reviewed by a senior
            administrator, never by the person who made the original decision.
          </p>
        </ProseSection>
      </Prose>
    </div>
  );
}
